// Profile image resolution for Who's Watching / Manage Profiles.
// 1. profile's own picture  2. Admin default (settings.default_profile_image)
// 3. built-in Pentecost+ icon shipped with the SPA.
export const BUILT_IN_DEFAULT_PROFILE_IMAGE = "/black-icon.png";

const LEGACY_BRAND_PATTERN = /aswa|\/assets\/img\/icon1\.png(\?.*)?$/i;

export function isLegacyBrandImage(url) {
  return LEGACY_BRAND_PATTERN.test(String(url || ""));
}

export function resolveDefaultProfileImage(configData) {
  const configured =
    configData && typeof configData.default_profile_image === "string"
      ? configData.default_profile_image.trim()
      : "";

  return configured && !isLegacyBrandImage(configured)
    ? configured
    : BUILT_IN_DEFAULT_PROFILE_IMAGE;
}

export function resolveProfilePicture(picture, configData) {
  const own = typeof picture === "string" ? picture.trim() : "";

  return own || resolveDefaultProfileImage(configData);
}

// Admin spotlight color (settings.profile_rail_highlight_color). Re-validated
// here with the backend's rule so only a plain #RRGGBB ever reaches CSS.
export const PROFILE_RAIL_HIGHLIGHT_FALLBACK = "#C9A227";

const HIGHLIGHT_HEX_PATTERN = /^#?[0-9A-Fa-f]{6}$/;

export function safeHighlightColor(value) {
  if (typeof value !== "string" || value.length > 7 || !HIGHLIGHT_HEX_PATTERN.test(value)) {
    return PROFILE_RAIL_HIGHLIGHT_FALLBACK;
  }

  return `#${value.replace("#", "").toUpperCase()}`;
}

// Admin "Profile Rail Appearance" numbers. Same bounds/defaults as the backend
// (ProfileRailHighlightColorService::APPEARANCE); anything non-numeric falls
// back to the default and numbers outside the bounds are clamped.
export const PROFILE_RAIL_APPEARANCE = {
  profile_rail_avatar_size: { min: 96, max: 200, decimals: 0, fallback: 176 },
  profile_rail_selected_scale: { min: 1, max: 1.4, decimals: 2, fallback: 1.3 },
  profile_rail_spotlight_size: { min: 1, max: 10, decimals: 2, fallback: 1.7 },
  profile_rail_spotlight_intensity: { min: 0, max: 100, decimals: 0, fallback: 65 },
  profile_rail_spotlight_blur: { min: 0, max: 40, decimals: 0, fallback: 24 },
  profile_rail_shape_size: { min: 0.8, max: 2, decimals: 2, fallback: 1.15 },
  profile_rail_shape_opacity: { min: 10, max: 80, decimals: 0, fallback: 35 },
};

// Decorative shape (settings.profile_rail_shape) → clip-path for the shape
// layer only; the profile image is never clipped. `custom` uses the Admin PNG
// (settings.profile_rail_custom_shape) as a mask and falls back to the default
// shape while no safe URL is available.
export const PROFILE_RAIL_SHAPE_DEFAULT = "none";

export const PROFILE_RAIL_SHAPES = {
  none: "none",
  circle: "circle(50% at 50% 50%)",
  triangle: "polygon(50% 4%, 97% 92%, 3% 92%)",
  square: "inset(0 round 10%)",
  star: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
  diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
  hexagon: "polygon(25% 3%, 75% 3%, 100% 50%, 75% 97%, 25% 97%, 0% 50%)",
  custom: "none",
};

const CUSTOM_SHAPE_URL_PATTERN = /^(https?:\/\/[^\s"'()\\<>]+|\/[^/\s"'()\\<>][^\s"'()\\<>]*)\.png$/i;

export function safeCustomShapeUrl(value) {
  return typeof value === "string" && value.length <= 2048 && CUSTOM_SHAPE_URL_PATTERN.test(value)
    ? value
    : "";
}

export function safeProfileRailShape(value, customUrl) {
  const shape =
    typeof value === "string" && Object.prototype.hasOwnProperty.call(PROFILE_RAIL_SHAPES, value)
      ? value
      : PROFILE_RAIL_SHAPE_DEFAULT;
  return shape === "custom" && !safeCustomShapeUrl(customUrl) ? PROFILE_RAIL_SHAPE_DEFAULT : shape;
}

const APPEARANCE_NUMBER_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

export function safeAppearanceNumber(key, value) {
  const rule = PROFILE_RAIL_APPEARANCE[key];
  if (!rule) return undefined;

  const text = typeof value === "number" ? String(value) : value;
  if (typeof text !== "string" || text.length > 6 || !APPEARANCE_NUMBER_PATTERN.test(text)) {
    return rule.fallback;
  }

  const factor = Math.pow(10, rule.decimals);
  const rounded = Math.round(parseFloat(text) * factor) / factor;
  return Math.min(Math.max(rounded, rule.min), rule.max);
}

export function profileRailAppearance(configData) {
  const config = configData || {};
  const values = {};
  Object.keys(PROFILE_RAIL_APPEARANCE).forEach((key) => {
    values[key] = safeAppearanceNumber(key, config[key]);
  });
  values.profile_rail_highlight_color = safeHighlightColor(config.profile_rail_highlight_color);
  values.profile_rail_custom_shape = safeCustomShapeUrl(config.profile_rail_custom_shape);
  values.profile_rail_shape = safeProfileRailShape(config.profile_rail_shape, values.profile_rail_custom_shape);
  return values;
}

// Style object for the profile page root; consumed only by profile-scoped CSS.
// Lengths are unitless px numbers so the CSS can derive responsive sizes.
export function profileRailAppearanceStyle(configData) {
  const a = profileRailAppearance(configData);
  return {
    "--profile-rail-highlight-color": a.profile_rail_highlight_color,
    "--profile-rail-avatar-size": String(a.profile_rail_avatar_size),
    "--profile-rail-selected-scale": String(a.profile_rail_selected_scale),
    "--profile-rail-spotlight-size": String(a.profile_rail_spotlight_size),
    "--profile-rail-spotlight-intensity": String(a.profile_rail_spotlight_intensity / 100),
    "--profile-rail-spotlight-blur": String(a.profile_rail_spotlight_blur),
    "--profile-rail-shape-size": String(a.profile_rail_shape_size),
    "--profile-rail-shape-opacity": String(
      a.profile_rail_shape === "none" ? 0 : a.profile_rail_shape_opacity / 100
    ),
    "--profile-rail-shape-clip": PROFILE_RAIL_SHAPES[a.profile_rail_shape],
    "--profile-rail-shape-mask":
      a.profile_rail_shape === "custom" ? `url("${a.profile_rail_custom_shape}")` : "none",
  };
}

// Categories that actually have thumbnails, in API order, de-duplicated by id.
export function profileImageRails(categories) {
  if (!Array.isArray(categories)) return [];

  return categories
    .map((category, index) => {
      const seen = {};
      const thumbnails = (Array.isArray(category && category.thumbnails)
        ? category.thumbnails
        : []
      ).filter((thumbnail) => {
        if (!thumbnail || thumbnail.id == null || !thumbnail.picture) return false;
        if (seen[thumbnail.id]) return false;
        seen[thumbnail.id] = true;
        return true;
      });

      return {
        id: category && category.id != null ? category.id : `category-${index}`,
        name: (category && (category.category_name || category.name)) || "",
        thumbnails,
      };
    })
    .filter((rail) => rail.thumbnails.length > 0);
}
